import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createBaseChartConfig, type ChartData } from './chartConfigUtils';
import ChartPlaceholder from './ChartPlaceholder';

interface DailyContractCountData {
    date: string;
    count: number;
    /** Extrapolated point (running month scaled to a full month). */
    projected?: boolean;
}

interface DailyContractCountChartProps {
    data: DailyContractCountData[];
    loading?: boolean;
    referenceValue?: number;
}

const DailyContractCountChart: React.FC<DailyContractCountChartProps> = ({
    data,
    loading = false,
    referenceValue,
}) => {
    const { t } = useTranslation();

    const chartData: ChartData[] = data.map(d => ({
        date: d.date,
        value: d.count ?? 0,
        projected: d.projected,
    }));
    const options: Highcharts.Options = createBaseChartConfig(
        chartData,
        {
            chartType: 'column',
            yAxisTitle: t('statistics.charts.numberOfContracts', 'Number of Contracts'),
            yAxisColor: '#52c41a',
            seriesName: t('statistics.charts.contractCount', 'Contract Count'),
            seriesColor: '#52c41a',
            referenceValue,
        },
        t,
    );

    // Show placeholder if loading or no data
    if (loading || !data || data.length === 0) {
        return (
            <ChartPlaceholder
                loading={loading}
                hasData={!!data && data.length > 0}
                noDataKey="statistics.messages.noData"
            />
        );
    }

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default DailyContractCountChart;
